import type { ColumnDef, Row } from '@tanstack/vue-table'
import { Button } from '~/components/ui/button'
import AlphabeticSortIcon from '~/components/AlphabeticSortIcon.vue'
import BarsArrowUp from '~/components/BarsArrowUp.vue'
import EnvelopeIcon from '~/components/EnvelopeIcon.vue'

interface DataOwner { name: string, id: number }

export const createColumns = (
  companyId: number | string,
  dataOwner: DataOwner,
  canEdit?: boolean,
): ColumnDef<PendingOwnerEntries>[] => {
  const columns: ColumnDef<PendingOwnerEntries>[] = [
    {
      accessorKey: 'asset',
      accessorFn: row => row.asset.name,
      id: 'asset.name',
      header: ({ column }) => {
      // 'asc', 'desc', or undefined
        const isSorted = column.getIsSorted()
        const sortIconStyling = isSorted === 'asc' ? 'text-green-500' : isSorted === 'desc' ? 'text-red-500 rotate-180' : ''

        return h('div', { class: 'flex justify-between items-center w-full' }, [
          h('span', { class: 'text-xs text-slate-800 font-semibold capitalize' }, 'asset'),
          h(Button, {
            class: 'text-main-primary',
            variant: 'ghost',
            onClick: () => column.toggleSorting(isSorted === 'asc', true),
          }, () => h(AlphabeticSortIcon, { customStyle: `size-5 ${sortIconStyling}` })),
        ])
      },
      cell: ({ row, column }) => h('div', { class: 'text-left text-md text-foreground line-clamp-1 no-localize-translate', title: row.getValue(column.id) }, row.getValue(column.id)),
    },
    {
      accessorKey: 'category',
      accessorFn: row => row.category.name,
      id: 'category.name',
      header: ({ column }) => {
      // 'asc', 'desc', or undefined
        const isSorted = column.getIsSorted()
        const sortIconStyling = isSorted === 'asc' ? 'text-green-500' : isSorted === 'desc' ? 'text-red-500 rotate-180' : ''

        return h('div', { class: 'flex justify-between items-center w-full' }, [
          h('span', { class: 'text-xs text-slate-800 font-semibold capitalize' }, 'category'),
          h(Button, {
            class: 'text-main-primary',
            variant: 'ghost',
            onClick: () => column.toggleSorting(isSorted === 'asc', true),
          }, () => h(AlphabeticSortIcon, { customStyle: `size-5 ${sortIconStyling}` })),
        ])
      },
      cell: ({ row, column }) => h('div', { class: 'text-left text-md text-foreground line-clamp-1', title: row.getValue(column.id) }, row.getValue(column.id)),
    },
    {
      accessorKey: 'missing_data_points',
      id: 'missing_data_points',
      header: ({ column }) => {
      // 'asc', 'desc', or undefined
        const isSorted = column.getIsSorted()
        const sortIconStyling = isSorted === 'asc' ? 'text-green-500' : isSorted === 'desc' ? 'text-red-500 !rotate-0' : ''

        return h('div', { class: 'flex justify-between items-center w-full' }, [
          h('span', { class: 'text-xs text-slate-800 font-semibold capitalize whitespace-nowrap' }, 'missing data points'),
          h(Button, {
            class: 'text-main-primary',
            variant: 'ghost',
            onClick: () => column.toggleSorting(column.getIsSorted() === 'asc', true),
          }, () => h(BarsArrowUp, { customStyle: `size-5 rotate-180 ${sortIconStyling}` })),
        ])
      },
      cell: ({ row, column }) => h('div', { class: 'text-left text-md text-foreground' }, row.getValue(column.id)),
    },
    {
      accessorKey: 'expected_data_points',
      id: 'expected_data_points',
      header: ({ column }) => {
      // 'asc', 'desc', or undefined
        const isSorted = column.getIsSorted()
        const sortIconStyling = isSorted === 'asc' ? 'text-green-500' : isSorted === 'desc' ? 'text-red-500 !rotate-0' : ''

        return h('div', { class: 'flex justify-between items-center w-full' }, [
          h('span', { class: 'text-xs text-slate-800 font-semibold capitalize whitespace-nowrap' }, 'expected data points'),
          h(Button, {
            class: 'text-main-primary',
            variant: 'ghost',
            onClick: () => column.toggleSorting(column.getIsSorted() === 'asc', true),
          }, () => h(BarsArrowUp, { customStyle: `size-5 rotate-180 ${sortIconStyling}` })),
        ])
      },
      cell: ({ row, column }) => h('div', { class: 'text-left text-md text-foreground' }, row.getValue(column.id)),
    },
    {
      accessorKey: 'period_missing_data_points',
      id: 'period_missing_data_points',
      header: ({ column }) => {
      // 'asc', 'desc', or undefined
        const isSorted = column.getIsSorted()
        const sortIconStyling = isSorted === 'asc' ? 'text-green-500' : isSorted === 'desc' ? 'text-red-500 !rotate-0' : ''

        return h('div', { class: 'flex justify-between items-center w-full' }, [
          h('span', { class: 'text-xs text-slate-800 font-semibold capitalize whitespace-nowrap' }, 'period missing data points'),
          h(Button, {
            class: 'text-main-primary',
            variant: 'ghost',
            onClick: () => column.toggleSorting(column.getIsSorted() === 'asc', true),
          }, () => h(BarsArrowUp, { customStyle: `size-5 rotate-180 ${sortIconStyling}` })),
        ])
      },
      cell: ({ row, column }) => h('div', { class: 'text-left text-md text-foreground line-clamp-1', title: row.getValue(column.id) }, row.getValue(column.id)),
    },
  ]

  if (canEdit !== false) {
    columns.push({
      id: 'actions',
      header: () => h('span', { class: 'max-w-min text-xs text-slate-800 font-semibold capitalize' }, 'actions'),
      cell: ({ row }) => {
        return h(Button, {
          class: 'text-main-primary',
          variant: 'link',
          onClick: () => sendIndividualEmail(row, companyId, dataOwner),
        }, () => h(EnvelopeIcon))
      },
    })
  }

  return columns
}

const sendIndividualEmail = (row: Row<PendingOwnerEntries>, companyId: number | string, dataOwner: DataOwner) => {
  const { protocol, host } = window.location
  const { $apis, $toast } = useNuxtApp()
  const { asset, category, period_missing_data_points } = row.original
  const requestData = {
    asset_id: asset.id,
    category_id: category.id,
    missing_period: period_missing_data_points,
    redirect_link: `${protocol}//${host}/dashboard/data-upload`,
  }

  $apis.progressTracker
    .sendPendingEntriesEmailReminder(companyId, dataOwner.id, requestData)
    .then(() => $toast.success(`Processing an email notification to ${dataOwner.name}`))
}
